import { ReactElement } from "react";
import {
  useGetCompanyDisplayQuery,
  useGetUserMenuQuery,
} from "types/generated/operations";
import { buildWebPath } from "lib/resource-finder";
import logoJOnlyUrl from "./assets/justworks-logo-j-only-onlight.svg?url";
import styles from "./sidebar-site-nav-1j.module.css";

const HELP_CENTER_URL = "https://help.justworks.com/hc/en-us";

function truncateCompanyName(name: string, maxLen: number): string {
  if (name.length <= maxLen) return name;
  return `${name.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

export default function Sidebar1J(): ReactElement {
  const { data: companyData, loading: companyLoading } =
    useGetCompanyDisplayQuery();
  const { data: userData, loading: userLoading } = useGetUserMenuQuery();

  if (companyLoading || userLoading) {
    return <></>;
  }

  const member = userData?.authenticatedMember;
  if (!companyData?.authenticatedMember || !member) {
    return <></>;
  }

  const companyName = companyData.authenticatedMember.company?.name ?? "";

  const displayFirst = member?.firstName?.trim() ?? "";
  const displayLast = member?.lastName?.trim() ?? "";
  const initials =
    displayFirst && displayLast
      ? `${displayFirst[0]}${displayLast[0]}`
      : displayFirst
      ? displayFirst.slice(0, 2).toUpperCase()
      : "";

  const friendlyLabel =
    displayFirst || displayLast ? `${displayFirst} ${displayLast}`.trim() : "";

  const photoUrl = member?.photoUrl ?? undefined;

  return (
    <nav className={styles.siteNav} aria-label="app-sidebar">
      <div className={styles.logoArea}>
        <a href={buildWebPath("/dashboard")} className={styles.logoRow}>
          <img
            src={logoJOnlyUrl}
            alt="Justworks Home"
            className={styles.jwLogoJ}
            width={20}
            height={20}
          />
          <span className={styles.companyName}>
            {truncateCompanyName(companyName, 40)}
          </span>
        </a>
      </div>

      <div className={styles.mainNav}>
        <div className={styles.navItem}>
          <button
            type="button"
            className={styles.workspaceButton}
            onClick={() =>
              window.history.length > 1
                ? window.history.back()
                : window.location.assign("/dashboard")
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                fill="#5e6167"
                d="M8.74991 16.25C8.66784 16.2501 8.58657 16.2339 8.51077 16.2024C8.43498 16.171 8.36615 16.1248 8.30825 16.0667L2.68325 10.4417C2.59519 10.3546 2.5352 10.2431 2.51099 10.1216C2.48679 10.0001 2.49947 9.87421 2.54741 9.76C2.57905 9.68453 2.6252 9.61601 2.68325 9.55833L8.30825 3.93333C8.36547 3.87192 8.43447 3.82267 8.51113 3.78851C8.5878 3.75435 8.67056 3.73598 8.75448 3.7345C8.8384 3.73302 8.92176 3.74846 8.99958 3.77989C9.0774 3.81133 9.1481 3.85811 9.20745 3.91746C9.26679 3.97681 9.31358 4.04751 9.34502 4.12533C9.37645 4.20315 9.39189 4.28651 9.39041 4.37043C9.38893 4.45435 9.37056 4.53711 9.3364 4.61378C9.30224 4.69044 9.25299 4.75944 9.19158 4.81666L4.63325 9.375H16.8749C17.0407 9.375 17.1996 9.44084 17.3169 9.55805C17.4341 9.67526 17.4999 9.83424 17.4999 10C17.4999 10.1658 17.4341 10.3247 17.3169 10.4419C17.1996 10.5591 17.0407 10.625 16.8749 10.625H4.63325L9.19158 15.1833C9.27888 15.2707 9.33832 15.3821 9.36239 15.5032C9.38646 15.6244 9.37408 15.75 9.32682 15.8641C9.27956 15.9782 9.19954 16.0758 9.09686 16.1445C8.99418 16.2132 8.87345 16.2499 8.74991 16.25Z"
              />
            </svg>
            Back
          </button>
        </div>
      </div>

      <div className={styles.memberSection}>
        <div className={styles.navItemGroup}>
          <div className={styles.navItem}>
            <a
              href={HELP_CENTER_URL}
              className={styles.workspaceButton}
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                viewBox="0 0 256 256"
                width="20"
                height="20"
                style={{ flexShrink: 0 }}
                aria-hidden
              >
                <path
                  fill="#5e6167"
                  d="M140 180a12 12 0 1 1-12-12a12 12 0 0 1 12 12M128 72c-22.06 0-40 16.15-40 36v4a8 8 0 0 0 16 0v-4c0-11 10.77-20 24-20s24 9 24 20s-10.77 20-24 20a8 8 0 0 0-8 8v8a8 8 0 0 0 16 0v-.72c18.24-3.35 32-17.9 32-35.28c0-19.85-17.94-36-40-36m104 56A104 104 0 1 1 128 24a104.11 104.11 0 0 1 104 104m-16 0a88 88 0 1 0-88 88a88.1 88.1 0 0 0 88-88"
                />
              </svg>
              Help
            </a>
          </div>
        </div>

        <div className={styles.usermenu}>
          <div className={styles.usermenuBtn}>
            <span
              className={styles.usermenuAvatar}
              style={
                photoUrl
                  ? {
                      backgroundImage: `url(${photoUrl})`,
                    }
                  : undefined
              }
              data-heap-redact-attributes="style"
              data-heap-redact-text="true"
            >
              {!photoUrl ? initials : null}
            </span>
            <span className={styles.usermenuName}>{friendlyLabel}</span>
          </div>
        </div>
      </div>
    </nav>
  );
}
